/**
 * Gameplay asset preloader.
 *
 * The roulette card and the question card both render PNG letters/stars
 * (S/U/P/E/R/T/★ + A-J) that are hosted on the Emergent customer-assets CDN.
 * In a school environment with multiple kids on a flaky WiFi network, the
 * FIRST time a letter image is needed it has to be fetched from the CDN —
 * which produces a visible flicker (the old letter image stays on screen
 * for ~100-1500ms while the new one downloads).
 *
 * To eliminate that flicker we eagerly preload ALL 17 images as soon as the
 * app boots, so they are in the browser's HTTP cache long before the user
 * reaches gameplay. Subsequent navigations / round transitions then serve
 * the images instantly from cache, regardless of network conditions.
 *
 * Mechanism: we create off-DOM `Image` objects and decode them. The browser
 * fires the requests in parallel, caches the responses, and the decoded
 * bitmaps are kept in the image cache so later `<img>` mounts paint in 1
 * paint frame.
 *
 * Idempotent — safe to call multiple times. Returns a Promise that resolves
 * when every asset has either loaded successfully or its request has
 * settled (we tolerate individual failures rather than blocking the app).
 */

import { BRAND_ASSETS } from "@/components/gameplay/parts";

// Local copy of ANSWER_ASSETS (the file keeps it module-private) so we can
// preload them here without forcing a refactor. URLs must stay in sync with
// the source of truth in parts.jsx.
const ANSWER_ASSETS = {
  A: "/game-letters/a.png",
  B: "/game-letters/b.png",
  C: "/game-letters/c.png",
  D: "/game-letters/d.png",
  E: "/game-letters/e.png",
  F: "/game-letters/f.png",
  G: "/game-letters/g.png",
  H: "/game-letters/h.png",
  I: "/game-letters/i.png",
  J: "/game-letters/j.png",
};

let preloadStarted = false;
let preloadPromise = null;
const _imageCache = new Map(); // keep refs alive so the bitmap stays decoded.

function preloadOne(url) {
  return new Promise((resolve) => {
    if (!url) return resolve({ url, ok: false, reason: "empty" });
    if (_imageCache.has(url)) return resolve({ url, ok: true, cached: true });
    const img = new Image();
    img.decoding = "async";
    img.loading = "eager";
    // Holding the reference prevents the browser from evicting the decoded
    // bitmap from memory when the off-DOM object would otherwise be GC'd.
    _imageCache.set(url, img);
    img.onload = () => {
      // Force decode so it is ready to paint in 1 frame on first DOM mount.
      if (typeof img.decode === "function") {
        img.decode().then(() => resolve({ url, ok: true })).catch(() => resolve({ url, ok: true, decodeFailed: true }));
      } else {
        resolve({ url, ok: true });
      }
    };
    img.onerror = () => {
      // Don't block app boot on a single 404 / blocked URL. Drop the cache
      // entry so a later retry isn't suppressed.
      _imageCache.delete(url);
      resolve({ url, ok: false, reason: "load-error" });
    };
    img.src = url;
  });
}

/**
 * Preload every gameplay asset. Returns a Promise resolving to an array of
 * `{url, ok}` records. Safe to call multiple times — subsequent calls return
 * the same Promise.
 */
export function preloadGameplayAssets() {
  if (preloadStarted) return preloadPromise;
  preloadStarted = true;
  const urls = [
    ...Object.values(BRAND_ASSETS || {}),
    ...Object.values(ANSWER_ASSETS),
  ].filter(Boolean);
  preloadPromise = Promise.all(urls.map(preloadOne));
  return preloadPromise;
}

/** Returns true if a given URL has been preloaded successfully (in our
 *  in-memory keep-alive cache). Useful for diagnostic logging or to gate UI
 *  states (e.g. hide a placeholder once assets are ready). */
export function isAssetReady(url) {
  return _imageCache.has(url);
}
